import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, CheckCircle, Camera, MapPin, Phone, Clock, Package, Upload, Loader2, ChevronRight, Wrench, LogOut, User, Navigation, ExternalLink, Route, CalendarPlus, KeyRound } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { AccountPasswordSection } from '../components/account/AccountPasswordSection';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function JobCard({ job, token, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [notes, setNotes] = useState('');
  const [expanded, setExpanded] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const uploadPhoto = async (file) => {
    if (!job.so_id) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('caption', '');
    try {
      await axios.post(`${API}/api/admin/sales-orders/${job.so_id}/delivery-photos`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded!');
      onUpdate?.();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const markComplete = async () => {
    if (!job.so_id) return;
    setCompleting(true);
    try {
      await axios.post(`${API}/api/installer/mark-complete/${job.so_id}`, { notes }, { headers });
      toast.success('Marked as complete!');
      onUpdate?.();
    } catch { toast.error('Failed to mark complete'); }
    finally { setCompleting(false); }
  };

  const isInstall = job.type === 'installation';
  const statusColor = job.status === 'installed' || job.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700';

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid={`job-card-${job.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isInstall ? 'bg-amber-100' : 'bg-blue-100'}`}>
          {isInstall ? <Wrench className="w-5 h-5 text-amber-600" /> : <Calendar className="w-5 h-5 text-blue-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-neutral-800 text-sm truncate">{job.title || `SO #${job.so_number}`}</p>
          <p className="text-xs text-neutral-500">{job.customer_name || 'Customer'}</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor}`}>{(job.status || 'scheduled').replace(/_/g, ' ')}</span>
        <ChevronRight className={`w-4 h-4 text-neutral-300 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t">
          {/* Contact info */}
          <div className="pt-3 space-y-1.5">
            {job.customer_phone && (
              <a href={`tel:${job.customer_phone}`} className="flex items-center gap-2 text-sm text-blue-600">
                <Phone className="w-4 h-4" /> {job.customer_phone}
              </a>
            )}
            {job.address && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600">
                <MapPin className="w-4 h-4" /> <span className="truncate">{job.address}</span>
              </a>
            )}
            {job.date && (
              <p className="flex items-center gap-2 text-sm text-neutral-600">
                <Clock className="w-4 h-4 text-neutral-400" />
                {new Date(job.date + 'T09:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                {job.time && ` at ${job.time}`}
              </p>
            )}
          </div>

          {/* Items */}
          {job.items?.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-neutral-500 uppercase">Items ({job.items.length})</p>
              {job.items.slice(0, 5).map((item, i) => {
                const cfg = item.config || item;
                return (
                  <div key={i} className="text-xs bg-neutral-50 rounded px-2 py-1.5 flex items-center gap-2">
                    <Package className="w-3 h-3 text-neutral-400 shrink-0" />
                    <span>{cfg.room_label || 'Shade'} — {cfg.width}" x {cfg.height}"</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Existing photos */}
          {job.delivery_photos?.length > 0 && (
            <div className="grid grid-cols-3 gap-1.5">
              {job.delivery_photos.map((p, i) => (
                <img key={i} src={`${API}${p.url}`} alt="" className="w-full h-16 object-cover rounded-lg border" />
              ))}
            </div>
          )}

          {/* Notes */}
          {job.notes && <p className="text-xs text-neutral-500 italic bg-neutral-50 rounded p-2">{job.notes}</p>}

          {/* Actions */}
          {isInstall && job.status !== 'installed' && job.status !== 'completed' && (
            <div className="space-y-2 pt-2">
              {/* Photo upload */}
              <label className="flex items-center justify-center gap-2 py-3 border border-dashed rounded-xl cursor-pointer bg-neutral-50 hover:bg-neutral-100 transition-colors" data-testid={`installer-photo-upload-${job.id}`}>
                {uploading ? <Loader2 className="w-5 h-5 animate-spin text-neutral-400" /> : <Camera className="w-5 h-5 text-neutral-400" />}
                <span className="text-sm text-neutral-500">{uploading ? 'Uploading...' : 'Take / Upload Photo'}</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0]); e.target.value = ''; }} disabled={uploading} />
              </label>

              {/* Notes + Mark Complete */}
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Installation notes (optional)..."
                className="w-full border rounded-xl p-3 text-sm resize-none h-16"
                data-testid={`installer-notes-${job.id}`}
              />
              <Button onClick={markComplete} disabled={completing} className="w-full bg-green-600 hover:bg-green-700 h-12 text-base" data-testid={`mark-complete-${job.id}`}>
                {completing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle className="w-5 h-5 mr-2" />}
                Mark Complete
              </Button>
            </div>
          )}

          {(job.status === 'installed' || job.status === 'completed') && (
            <div className="flex items-center gap-2 py-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium text-sm">Installation Complete</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RoutePlannerPanel({ token }) {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchRoute = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/installer/route-plan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoute(res.data);
    } catch {
      toast.error('Failed to load route plan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoute(); }, [token]);

  if (loading) return (
    <div className="bg-white rounded-xl border p-4 flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
    </div>
  );

  if (!route || route.job_count === 0) return null;

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="route-planner-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 flex items-center gap-3"
        data-testid="route-planner-toggle"
      >
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
          <Navigation className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-neutral-800 text-sm">Route Planner</p>
          <p className="text-xs text-neutral-500">{route.job_count} stop{route.job_count !== 1 ? 's' : ''} today</p>
        </div>
        <ChevronRight className={`w-4 h-4 text-neutral-300 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t">
          {/* Optimized stop list */}
          <div className="pt-3 space-y-2">
            {route.jobs.map((job, idx) => (
              <div key={job.id || idx} className="flex items-start gap-3" data-testid={`route-stop-${idx}`}>
                <div className="flex flex-col items-center">
                  <div className="w-7 h-7 rounded-full bg-neutral-900 text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {idx + 1}
                  </div>
                  {idx < route.jobs.length - 1 && <div className="w-0.5 h-6 bg-neutral-200 mt-1" />}
                </div>
                <div className="flex-1 min-w-0 pb-1">
                  <p className="text-sm font-medium text-neutral-800 truncate">{job.title || job.customer_name}</p>
                  {job.time && <p className="text-xs text-neutral-500"><Clock className="w-3 h-3 inline mr-1" />{job.time}</p>}
                  {job.address && (
                    <a href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 truncate block mt-0.5">
                      <MapPin className="w-3 h-3 inline mr-1" />{job.address}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Open full route in Google Maps */}
          {route.route_url && (
            <a
              href={route.route_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors"
              data-testid="open-route-maps-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Open Route in Google Maps
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function AvailabilityPanel({ token }) {
  const [expanded, setExpanded] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newDayOff, setNewDayOff] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const fetchAvailability = () => {
    setLoading(true);
    axios.get(`${API}/api/installer/availability`, { headers })
      .then(res => setAvailability(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAvailability(); }, [token]);

  const save = async (updates) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/installer/availability`, updates, { headers });
      setAvailability(prev => ({ ...prev, ...updates }));
      toast.success('Availability updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleDay = (day) => {
    if (!availability) return;
    const current = availability.working_days || [1,2,3,4,5];
    const updated = current.includes(day) ? current.filter(d => d !== day) : [...current, day].sort();
    save({ working_days: updated });
  };

  const addDayOff = () => {
    if (!newDayOff || !availability) return;
    const updated = [...(availability.days_off || []), newDayOff].filter((v, i, a) => a.indexOf(v) === i).sort();
    save({ days_off: updated });
    setNewDayOff('');
  };

  const removeDayOff = (date) => {
    if (!availability) return;
    save({ days_off: (availability.days_off || []).filter(d => d !== date) });
  };

  if (loading || !availability) return null;

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="availability-panel">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-4 flex items-center gap-3" data-testid="availability-toggle">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
          <Clock className="w-5 h-5 text-green-600" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-neutral-800 text-sm">My Availability</p>
          <p className="text-xs text-neutral-500">
            {availability.working_hours?.start || '08:00'} – {availability.working_hours?.end || '17:00'}
            {(availability.days_off?.length || 0) > 0 && ` · ${availability.days_off.length} day${availability.days_off.length > 1 ? 's' : ''} off`}
          </p>
        </div>
        <ChevronRight className={`w-4 h-4 text-neutral-300 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t pt-3">
          {/* Working Hours */}
          <div>
            <p className="text-xs font-semibold text-neutral-500 uppercase mb-2">Working Hours</p>
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={availability.working_hours?.start || '08:00'}
                onChange={e => save({ working_hours: { ...availability.working_hours, start: e.target.value } })}
                className="border rounded-lg px-3 py-2 text-sm flex-1"
                data-testid="working-hours-start"
              />
              <span className="text-neutral-400 text-sm">to</span>
              <input
                type="time"
                value={availability.working_hours?.end || '17:00'}
                onChange={e => save({ working_hours: { ...availability.working_hours, end: e.target.value } })}
                className="border rounded-lg px-3 py-2 text-sm flex-1"
                data-testid="working-hours-end"
              />
            </div>
          </div>

          {/* Working Days */}
          <div>
            <p className="text-xs font-semibold text-neutral-500 uppercase mb-2">Working Days</p>
            <div className="flex gap-1.5">
              {DAY_NAMES.map((name, idx) => {
                const active = (availability.working_days || [1,2,3,4,5]).includes(idx);
                return (
                  <button
                    key={idx}
                    onClick={() => toggleDay(idx)}
                    disabled={saving}
                    className={`w-10 h-10 rounded-full text-xs font-medium transition-colors ${active ? 'bg-green-600 text-white' : 'bg-neutral-100 text-neutral-400'}`}
                    data-testid={`working-day-${idx}`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Days Off */}
          <div>
            <p className="text-xs font-semibold text-neutral-500 uppercase mb-2">Days Off</p>
            <div className="flex gap-2 mb-2">
              <input
                type="date"
                value={newDayOff}
                onChange={e => setNewDayOff(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="border rounded-lg px-3 py-2 text-sm flex-1"
                data-testid="day-off-input"
              />
              <Button size="sm" onClick={addDayOff} disabled={!newDayOff || saving} className="bg-neutral-900 text-white" data-testid="add-day-off-btn">
                Add
              </Button>
            </div>
            {(availability.days_off || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {availability.days_off.map(d => (
                  <span key={d} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs px-2.5 py-1 rounded-full border border-red-200">
                    {new Date(d + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    <button onClick={() => removeDayOff(d)} className="hover:text-red-900" data-testid={`remove-day-off-${d}`}>&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function InstallerMobilePage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem('admin_token') || localStorage.getItem('token') || '');
  const [showPw, setShowPw] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSchedule = () => {
    if (!token) { setLoading(false); setError('login'); return; }
    setLoading(true);
    axios.get(`${API}/api/installer/my-schedule`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setSchedule(res.data); setError(null); })
      .catch(err => {
        if (err.response?.status === 401) setError('login');
        else setError('Failed to load schedule');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSchedule(); }, [token]);

  if (error === 'login' || !token) {
    return <InstallerLogin onLogin={(t) => { setToken(t); localStorage.setItem('token', t); }} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  const allJobs = [...(schedule?.today || []), ...(schedule?.upcoming || [])];

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="installer-mobile-page">
      <Dialog open={showPw} onOpenChange={setShowPw}>
        <DialogContent className="max-w-md">
          <AccountPasswordSection token={token} onNewToken={(t) => { localStorage.setItem('token', t); localStorage.removeItem('admin_token'); setToken(t); setShowPw(false); }} />
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div className="bg-neutral-900 text-white px-4 py-4 flex items-center justify-between safe-area-top">
        <div>
          <h1 className="text-lg font-bold tracking-wider" style={{ fontFamily: 'Georgia, serif' }}>APOLLO SHADES</h1>
          <p className="text-amber-500 text-xs">Installer Portal</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <p className="text-xs text-neutral-400">Welcome,</p>
            <p className="text-sm font-medium">{schedule?.installer_name || 'Installer'}</p>
          </div>
          <button onClick={() => setShowPw(true)} className="p-2 text-neutral-400 hover:text-white" aria-label="Change password" data-testid="installer-change-password-btn">
            <KeyRound className="w-4 h-4" />
          </button>
          <button onClick={() => { localStorage.removeItem('token'); setToken(''); }} className="p-2 text-neutral-400 hover:text-white">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Today's count + Calendar export */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
            <span className="text-xl font-bold text-amber-700">{schedule?.today_count || 0}</span>
          </div>
          <div className="flex-1">
            <p className="font-medium text-amber-800">Today's Jobs</p>
            <p className="text-xs text-amber-600">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          </div>
          {(schedule?.today_count || 0) > 0 && (
            <a
              href={`${API}/api/installer/calendar.ics?authorization=${encodeURIComponent('Bearer ' + token)}`}
              download
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-amber-300 rounded-lg text-amber-700 text-xs font-medium hover:bg-amber-100 transition-colors"
              data-testid="add-to-calendar-btn"
              onClick={(e) => {
                e.preventDefault();
                axios.get(`${API}/api/installer/calendar.ics`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: 'blob'
                }).then(res => {
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `schedule_${new Date().toISOString().slice(0,10)}.ics`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Calendar file downloaded');
                }).catch(() => toast.error('Failed to export calendar'));
              }}
            >
              <CalendarPlus className="w-4 h-4" />
              Calendar
            </a>
          )}
        </div>

        {/* Route Planner */}
        <RoutePlannerPanel token={token} />

        {/* Availability Settings */}
        <AvailabilityPanel token={token} />

        {/* Today's Jobs */}
        {schedule?.today?.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-neutral-600 mb-2 uppercase tracking-wide">Today</h2>
            <div className="space-y-2">
              {schedule.today.map(job => (
                <JobCard key={job.id} job={job} token={token} onUpdate={fetchSchedule} />
              ))}
            </div>
          </div>
        )}

        {/* Upcoming */}
        {schedule?.upcoming?.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-neutral-600 mb-2 uppercase tracking-wide">Upcoming</h2>
            <div className="space-y-2">
              {schedule.upcoming.map(job => (
                <JobCard key={job.id} job={job} token={token} onUpdate={fetchSchedule} />
              ))}
            </div>
          </div>
        )}

        {allJobs.length === 0 && (
          <div className="py-12 text-center text-neutral-400">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No scheduled jobs</p>
            <p className="text-xs mt-1">Check back later for upcoming installations</p>
          </div>
        )}
      </div>
    </div>
  );
}

function InstallerLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email, password });
      const token = res.data.access_token;
      if (token) {
        onLogin(token);
      } else {
        toast.error('Login failed');
      }
    } catch { toast.error('Invalid credentials'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-900 flex items-center justify-center px-4" data-testid="installer-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-wider text-white" style={{ fontFamily: 'Georgia, serif' }}>APOLLO SHADES</h1>
          <p className="text-amber-500 text-sm mt-1">Installer Portal</p>
        </div>
        <form onSubmit={handleLogin} className="bg-white rounded-2xl p-6 space-y-4 shadow-xl">
          <div>
            <label className="text-sm font-medium text-neutral-700 block mb-1">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full border rounded-xl px-4 py-3 text-sm" placeholder="you@company.com" data-testid="installer-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700 block mb-1">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full border rounded-xl px-4 py-3 text-sm" placeholder="Your password" data-testid="installer-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 h-12 text-base rounded-xl">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}
